import React, { useState } from "react";
import { Modal, Rate, Input, Tag, Spin } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import CustomButton from "../ui/CustomButton/CustomButton";
import ratingIcon from "../../assets/images/rating.png";
import "./LeaveARatingModal.scss";
import { useTranslation } from "react-i18next";
import { TAttendedClassEvent, ClubpassEventType } from "../../models/ClassEvent";
import { useReviewMutations } from "../../api/mutations/useReviewMutations";

type EventFmtOpts = {
  durationMin?: number;
  locale?: string;
  timeZone?: string;
};

export function formatEventDate(iso: string, opts: EventFmtOpts = {}): string {
  if (!iso) return "";
  const { durationMin, locale = "en-US", timeZone } = opts;
  const d = new Date(iso);

  const datePart = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  }).format(d);

  const timePart = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).format(d);

  const base = `${datePart} – ${timePart}`;
  return durationMin ? `${base} (${durationMin} min)` : base;
}

interface LeaveARatingModalProps {
  open: boolean;
  event: TAttendedClassEvent | null;
  onClose: () => void;
  onSuccessLeaveARating: () => void;
}

const feedbackTags = ["GreatClass", "AmazingInstructor!", "ClassWasntAsGoodAsIExpected"];

const LeaveARatingModal: React.FC<LeaveARatingModalProps> = ({
  open,
  event,
  onClose,
  onSuccessLeaveARating,
}) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { leaveAReview } = useReviewMutations();

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const isSelected = prev.includes(tag);
      const newTags = isSelected ? prev.filter((t) => t !== tag) : [...prev, tag];

      // Auto-fill comment with selected tags
      setComment(newTags.join(" "));
      return newTags;
    });
  };

  const handleSubmit = () => {
    setIsLoading(true);
    leaveAReview(
      {
        gymListingId: event?.gymListingId || "",
        comment,
        rating,
      },
      {
        onSuccess: () => {
          setSubmitted(true);
        },
        onSettled: () => {
          setIsLoading(false);
        },
        onError: handleClose,
      }
    );
  };

  const handleClose = () => {
    setRating(0);
    setSelectedTags([]);
    setComment("");
    setSubmitted(false);
    onClose();
  };

  const onPressCloseAfterSuccess = () => {
    onSuccessLeaveARating();
    setRating(0);
    setSelectedTags([]);
    setComment("");
    setSubmitted(false);
    onClose();
  };

  let dateTimeText = "";
  if (event?.type === ClubpassEventType.GROUP_CLASS) {
    dateTimeText = formatEventDate(event.occuranceDate ?? "", {
      durationMin: event.duration,
      timeZone: "Europe/Paris",
    });
  } else if (event?.type === ClubpassEventType.OPEN_GYM) {
    const start = formatEventDate(event.startDate ?? "", {
      timeZone: "Europe/Paris",
    });
    const end = event.endDate
      ? new Intl.DateTimeFormat("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: "Europe/Paris",
        }).format(new Date(event.endDate))
      : "";
    dateTimeText = end ? `${start} - ${end}` : start;
  }

  return (
    <Modal
      open={open}
      footer={null}
      closable
      centered
      onCancel={handleClose}
      className="leave-rating-modal"
    >
      {!submitted ? (
        <div className="leave-rating">
          <h2 className="leave-rating__title">{t("LeaveARating")}</h2>

          <div className="leave-rating__class">
            <p className="leave-rating__class-title">{event?.gymName}</p>
            <p className="leave-rating__class-date">
              <CalendarOutlined /> {dateTimeText}
            </p>
          </div>

          <Rate value={rating} onChange={setRating} className="leave-rating__stars" />

          <div className="leave-rating__tags">
            {feedbackTags.map((tag) => (
              <Tag
                key={t(tag)}
                onClick={() => toggleTag(t(tag))}
                className={`leave-rating__tag ${
                  selectedTags.includes(t(tag)) ? "leave-rating__tag--selected" : ""
                }`}
              >
                {t(tag)}
              </Tag>
            ))}
          </div>

          <Input.TextArea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t("AddAComment")}
            rows={4}
            className="leave-rating__textarea"
          />

          <CustomButton
            text={t("Submit")}
            type="button"
            typeStyle="fill"
            functionOnClick={handleSubmit}
            showLoading={isLoading}
          />

          {isLoading && (
            <div className="leave-rating__overlay">
              <Spin size="large" />
            </div>
          )}
        </div>
      ) : (
        <div className="leave-rating__thankyou">
          <img src={ratingIcon} alt="celebration" className="leave-rating__thankyou-icon" />
          <h2 className="leave-rating__thankyou-title">{t("ThanksForRating")}</h2>
          <p className="leave-rating__thankyou-text">{t("ThanksForRatingDesc")}</p>

          <p className="leave-rating__thankyou-close" onClick={onPressCloseAfterSuccess}>
            {t("Close")}
          </p>
        </div>
      )}
    </Modal>
  );
};

export default LeaveARatingModal;
